import {
  buildListingTitle,
} from '../../util/fieldHelpers';

/**
 * BulkUploadListingsModal.helpers.js
 *
 * CSV headers are human-friendly labels. The parser maps them to internal
 * field names before any validation or payload building runs.
 *
 * CSV header → internal field name mapping:
 *   Category    → categoryLevel1
 *   Make        → categoryLevel2
 *   Model       → categoryLevel3
 *   Year Model  → year
 *   Hours       → hours
 *   Location    → loc
 *   Description → description
 *   Price       → price
 *
 * Fixed values (never in CSV — injected automatically):
 *   currency                = 'USD'
 *   listingType             = 'free-listing'
 *   transactionProcessAlias = 'default-inquiry/release-1'
 *   unitType                = 'inquiry'
 *
 * Title auto-generated as:
 *   "{categoryLevel1} {year} {categoryLevel2} {categoryLevel3} {hours} Hr/Hrs"
 */

export const FIXED_CURRENCY = 'USD';
export const FIXED_LISTING_TYPE = 'free-listing';
export const FIXED_TRANSACTION_PROCESS_ALIAS = 'default-inquiry/release-1';
export const FIXED_UNIT_TYPE = 'inquiry';

// ---------------------------------------------------------------------------
// Header → field name map
// Key   = exactly what appears in the CSV header row (case-insensitive match)
// Value = internal field name used everywhere else in the code
// ---------------------------------------------------------------------------

export const CSV_HEADER_MAP = {
  'category':    'categoryLevel1',
  'make':        'categoryLevel2',
  'model':       'categoryLevel3',
  'year model':  'year',
  'hours':       'hours',
  'location':    'loc',
  'description': 'description',
  'price':       'price',
};

// Human-friendly labels in the order they appear in the template
export const CSV_HEADERS = [
  'Category',
  'Make',
  'Model',
  'Year Model',
  'Hours',
  'Location',
  'Description',
  'Price',
];

// ---------------------------------------------------------------------------
// CSV Template
// ---------------------------------------------------------------------------

export const generateCSVTemplate = () => {
  const examples = [
    [
      'Aerial Equipment',
      'Genie',
      'AWP25\'s',
      '2020',
      '8',
      'AL',
      'Well-maintained Genie AWP25\'s is a dependable solution for professionals who need safe, efficient access to elevated work areas without the bulk of larger lifts.',
      '1200',
    ],
    [
      'Aerial Equipment',
      'Genie',
      'AWP36\'s',
      '2018',
      '40',
      'CA',
      'The Genie AWP36\'s offers a practical and dependable solution for professionals who require safe access to higher work areas while maintaining mobility and ease of use.',
      '8500',
    ],
  ];

  const rows = [CSV_HEADERS, ...examples];
  return rows
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
};

// ---------------------------------------------------------------------------
// CSV Parsing
// ---------------------------------------------------------------------------

const parseCSVText = text => {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const nonEmpty = lines.filter(l => l.trim() !== '');
  if (nonEmpty.length < 2) return [];

  const parseRow = row => {
    const cells = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < row.length; i++) {
      const ch = row[i];
      if (inQuotes) {
        if (ch === '"') {
          if (row[i + 1] === '"') { cur += '"'; i++; }
          else { inQuotes = false; }
        } else { cur += ch; }
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        cells.push(cur.trim()); cur = '';
      } else { cur += ch; }
    }
    cells.push(cur.trim());
    return cells;
  };

  const rawHeaders = parseRow(nonEmpty[0]).map(h => h.trim());

  return nonEmpty.slice(1).map(line => {
    const cells = parseRow(line);
    const obj = {};
    rawHeaders.forEach((h, i) => {
      // Map the human-friendly CSV header to the internal field name.
      // Falls back to the raw header itself if no mapping exists.
      const fieldName = CSV_HEADER_MAP[h.toLowerCase()] || h;
      obj[fieldName] = cells[i] !== undefined ? cells[i] : '';
    });
    return obj;
  });
};

export const parseCSVFile = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try { resolve(parseCSVText(e.target.result)); }
      catch (err) { reject(new Error('Failed to parse CSV: ' + err.message)); }
    };
    reader.onerror = () => reject(new Error('Could not read file.'));
    reader.readAsText(file);
  });
};

// ---------------------------------------------------------------------------
// Validation  (works on internal field names after parsing)
// ---------------------------------------------------------------------------

// Internal field names that must be present and non-empty
const REQUIRED_FIELDS = [
  'categoryLevel1',
  'categoryLevel2',
  'categoryLevel3',
  'year',
  'hours',
  'loc',
  'description',
  'price',
];

// Maps internal field name → translation key suffix used in error messages
const FIELD_LABEL_KEY = {
  categoryLevel1: 'category',
  categoryLevel2: 'make',
  categoryLevel3: 'model',
  year:           'yearModel',
  hours:          'hours',
  loc:            'location',
  description:    'description',
  price:          'price',
};

/**
 * Validate parsed CSV rows.
 * Pass the `intl` object from useIntl() so all error messages come from translations.
 * Returns an array of { row, message } error objects. Empty array = valid.
 *
 * @param {{ [key: string]: string }[]} rows
 * @param {Object} intl - react-intl intl object
 * @returns {{ row: number, message: string }[]}
 */
export const validateCSVData = (rows, intl) => {
  const t = (id, values) => intl.formatMessage({ id }, values);
  const errors = [];

  if (!rows || rows.length === 0) {
    errors.push({ row: 0, message: t('BulkUploadListingsModal.validation.emptyFile') });
    return errors;
  }

  // Check all required internal fields are present (i.e. all headers were mapped correctly)
  const presentFields = Object.keys(rows[0]);
  REQUIRED_FIELDS.forEach(field => {
    if (!presentFields.includes(field)) {
      errors.push({
        row: 0,
        message: t('BulkUploadListingsModal.validation.missingColumn', {
          column: t(`BulkUploadListingsModal.fieldGuide.field.${FIELD_LABEL_KEY[field]}.name`),
        }),
      });
    }
  });

  if (errors.length > 0) return errors;

  rows.forEach((row, idx) => {
    const rowNum = idx + 2; // +2: row 1 is header, idx is 0-based

    // Required field presence
    REQUIRED_FIELDS.forEach(field => {
      if (!row[field] || row[field].trim() === '') {
        errors.push({
          row: rowNum,
          message: t('BulkUploadListingsModal.validation.fieldRequired', {
            field: t(`BulkUploadListingsModal.fieldGuide.field.${FIELD_LABEL_KEY[field]}.name`),
          }),
        });
      }
    });

    // Hours: positive number
    if (row.hours) {
      const h = Number(row.hours);
      if (isNaN(h) || h <= 0) {
        errors.push({
          row: rowNum,
          message: t('BulkUploadListingsModal.validation.hoursInvalid', { value: row.hours }),
        });
      }
    }

    // Price: positive integer (USD cents)
    if (row.price) {
      const p = parseInt(row.price, 10);
      if (isNaN(p) || p <= 0 || String(p) !== row.price.trim()) {
        errors.push({
          row: rowNum,
          message: t('BulkUploadListingsModal.validation.priceInvalid', { value: row.price }),
        });
      }
    }

    // Year Model: 4-digit year
    if (row.year && !/^\d{4}$/.test(row.year.trim())) {
      errors.push({
        row: rowNum,
        message: t('BulkUploadListingsModal.validation.yearInvalid', { value: row.year }),
      });
    }

    // Auto-generated title length guard
    const title = buildListingTitle(row);
    if (title.length > 255) {
      errors.push({
        row: rowNum,
        message: t('BulkUploadListingsModal.validation.titleTooLong', { title }),
      });
    }
  });

  return errors;
};

// ---------------------------------------------------------------------------
// Payload builder — Sharetribe Marketplace API (sdk.ownListings.create)
// ---------------------------------------------------------------------------

export const rowToListingPayload = row => {
  const title = buildListingTitle(row);

  const publicData = {
    categoryLevel1: row.categoryLevel1.trim(),
    categoryLevel2: row.categoryLevel2.trim(),
    categoryLevel3: row.categoryLevel3.trim(),
    hours: Number(row.hours),
    loc: row.loc.trim(),
    year: row.year.trim(),
    listingType: FIXED_LISTING_TYPE,
    transactionProcessAlias: FIXED_TRANSACTION_PROCESS_ALIAS,
    unitType: FIXED_UNIT_TYPE,
  };

  return {
    title,
    description: row.description.trim(),
    price: {
      amount: parseInt(row.price, 10),
      currency: FIXED_CURRENCY,
    },
    publicData,
  };
};
